import { getTaxonomyStatusConfig } from '@constants/applicationStatus';
import { Chip } from '@mui/material';

interface CRMStatusChipProps {
  status: string;
  /**
   * Override the word, never the colour.
   *
   * A guide's ACTIVE reads "Published" and a rule's reads "Active"; they are the same state and
   * should be the same green. Before this, the only way to say "Published" was a local colour map,
   * which is how one vocabulary became ten. Reach for this when a domain has its own word, and add
   * a key to STATUS_MAP when it has its own state.
   */
  label?: string;
  /**
   * "application": the value is a candidate stage, so the stage colours win. Without it,
   * "Screening", "Interview", "Closed" and "Active" are caught by the generic map first, and a
   * Closed candidate reads the same grey as a closed job. The generic map stays first for every
   * other kind, where those words mean something else.
   */
  kind?: 'application';
}

interface StatusConfig {
  label: string;
  sx: {
    backgroundColor: string;
    color: string;
    borderColor?: string;
  };
}

const STATUS_MAP: Record<string, StatusConfig> = {
  // Application statuses
  applied: {
    label: 'Applied',
    sx: { backgroundColor: 'var(--color-stage-qualified-bg)', color: 'var(--color-stage-qualified-text)' },
  },
  screening: {
    label: 'Screening',
    sx: { backgroundColor: 'var(--color-stage-lead-bg)', color: 'var(--color-stage-lead-text)' },
  },
  interviewing: {
    label: 'Interviewing',
    sx: { backgroundColor: 'var(--color-stage-proposal-bg)', color: 'var(--color-stage-proposal-text)' },
  },
  interview: {
    label: 'Interview',
    sx: { backgroundColor: 'var(--color-stage-proposal-bg)', color: 'var(--color-stage-proposal-text)' },
  },
  offered: {
    label: 'Offered',
    sx: { backgroundColor: 'var(--color-stage-negotiation-bg)', color: 'var(--color-stage-negotiation-text)' },
  },
  hired: {
    label: 'Hired',
    sx: { backgroundColor: 'var(--color-stage-won-bg)', color: 'var(--color-stage-won-text)' },
  },
  rejected: {
    label: 'Rejected',
    sx: { backgroundColor: 'var(--color-stage-lost-bg)', color: 'var(--color-stage-lost-text)' },
  },
  withdrawn: {
    label: 'Withdrawn',
    sx: { backgroundColor: 'var(--color-background-muted)', color: 'var(--color-text-muted)' },
  },
  // Company approval statuses
  approved: {
    label: 'Approved',
    sx: { backgroundColor: 'var(--color-status-success-bg)', color: 'var(--color-status-success-text)' },
  },
  pending: {
    label: 'Pending',
    sx: { backgroundColor: 'var(--color-status-warning-bg)', color: 'var(--color-status-warning-text)' },
  },
  suspended: {
    label: 'Suspended',
    sx: { backgroundColor: 'var(--color-stage-lost-bg)', color: 'var(--color-stage-lost-text)' },
  },
  // Generic
  active: {
    label: 'Active',
    sx: { backgroundColor: 'var(--color-status-success-bg)', color: 'var(--color-status-success-text)' },
  },
  inactive: {
    label: 'Inactive',
    sx: { backgroundColor: 'var(--color-background-muted)', color: 'var(--color-text-muted)' },
  },
  closed: {
    label: 'Closed',
    sx: { backgroundColor: 'var(--color-background-muted)', color: 'var(--color-text-muted)' },
  },
  open: {
    label: 'Open',
    sx: { backgroundColor: 'var(--color-stage-qualified-bg)', color: 'var(--color-stage-qualified-text)' },
  },
  /*
   * The third member of the JobStatus enum {OPEN, CLOSED, EXPIRED}, and the only one that had no
   * entry -- so an expired job fell through to the generic branch and rendered an uncoloured
   * chip. Amber rather than the grey of `closed`: a job someone deliberately closed is finished,
   * a job that lapsed is a prompt to renew or close it.
   */
  expired: {
    label: 'Expired',
    sx: { backgroundColor: 'var(--color-status-warning-bg)', color: 'var(--color-status-warning-text)' },
  },
  /* A promo code that has reached its redemption cap. Amber like `expired`: still switched on, but
     handing nothing out, which is a prompt to raise the cap or retire the code. */
  full: {
    label: 'Full',
    sx: { backgroundColor: 'var(--color-status-warning-bg)', color: 'var(--color-status-warning-text)' },
  },
  // Rule, queue and content states. `paused` is not `inactive`: a paused rule is deliberately
  // held and resumable, which is an amber, where inactive is a grey.
  // Job requirement status (New / Active / On Hold / Filled / Closed); Active and Closed are above.
  new: {
    label: 'New',
    sx: { backgroundColor: 'var(--color-stage-lead-bg)', color: 'var(--color-stage-lead-text)' },
  },
  onhold: {
    label: 'On Hold',
    sx: { backgroundColor: 'var(--color-status-warning-bg)', color: 'var(--color-status-warning-text)' },
  },
  filled: {
    label: 'Filled',
    sx: { backgroundColor: 'var(--color-stage-won-bg)', color: 'var(--color-stage-won-text)' },
  },
  paused: {
    label: 'Paused',
    sx: { backgroundColor: 'var(--color-status-warning-bg)', color: 'var(--color-status-warning-text)' },
  },
  skipped: {
    label: 'Skipped',
    sx: { backgroundColor: 'var(--color-status-warning-bg)', color: 'var(--color-status-warning-text)' },
  },
  /*
   * Email delivery (SES). Final problems -- a hard bounce, a spam complaint, an address we no
   * longer mail -- are the loss red; the kind that may clear up (a transient bounce, repeated soft
   * bounces) is amber. DeliveryChip used to restate these as its own --red/--amber, a second red
   * beside every other failure chip.
   */
  deliveryfinal: {
    label: 'Blocked',
    sx: { backgroundColor: 'var(--color-stage-lost-bg)', color: 'var(--color-stage-lost-text)' },
  },
  deliverywarning: {
    label: 'Soft bounce',
    sx: { backgroundColor: 'var(--color-status-warning-bg)', color: 'var(--color-status-warning-text)' },
  },
  failed: {
    label: 'Failed',
    sx: { backgroundColor: 'var(--color-stage-lost-bg)', color: 'var(--color-stage-lost-text)' },
  },
  success: {
    label: 'Success',
    sx: { backgroundColor: 'var(--color-status-success-bg)', color: 'var(--color-status-success-text)' },
  },
  draft: {
    label: 'Draft',
    sx: { backgroundColor: 'var(--color-status-warning-bg)', color: 'var(--color-status-warning-text)' },
  },
  archived: {
    label: 'Archived',
    sx: { backgroundColor: 'var(--color-background-muted)', color: 'var(--color-text-muted)' },
  },
  // Agreement statuses. Added because both agreement surfaces had a local colour map returning
  // MUI's semantic palette, which is a different green and a different amber from the tokens
  // every other chip in the console uses. Without these two keys they fall through to the muted
  // default, which reads as "unknown" for two states that are perfectly well known.
  superseded: {
    label: 'Superseded',
    sx: { backgroundColor: 'var(--color-background-muted)', color: 'var(--color-text-muted)' },
  },
  terminated: {
    label: 'Terminated',
    sx: { backgroundColor: 'var(--color-stage-lost-bg)', color: 'var(--color-stage-lost-text)' },
  },
  // External job sources and their runs (mintly-external-jobs-service). NEEDS_REVIEW normalises
  // to `needsreview`; `succeeded` is the run word for what `success` means elsewhere.
  needsreview: {
    label: 'Needs review',
    sx: { backgroundColor: 'var(--color-status-warning-bg)', color: 'var(--color-status-warning-text)' },
  },
  declined: {
    label: 'Declined',
    sx: { backgroundColor: 'var(--color-stage-lost-bg)', color: 'var(--color-stage-lost-text)' },
  },
  // A job's or employer's MANAGED mode. The service is Premium Hiring on screen; the value is not.
  managed: {
    label: 'Premium Hiring',
    sx: { backgroundColor: 'var(--color-background-muted)', color: 'var(--color-text-secondary)' },
  },
  // A purchase's state (Subscriptions). Cancelled is finished, the grey of `closed`; refunded is
  // money handed back, which is the loss colour rather than a neutral one.
  cancelled: {
    label: 'Cancelled',
    sx: { backgroundColor: 'var(--color-background-muted)', color: 'var(--color-text-muted)' },
  },
  refunded: {
    label: 'Refunded',
    sx: { backgroundColor: 'var(--color-stage-lost-bg)', color: 'var(--color-stage-lost-text)' },
  },
  succeeded: {
    label: 'Succeeded',
    sx: { backgroundColor: 'var(--color-status-success-bg)', color: 'var(--color-status-success-text)' },
  },
};

function getStatusConfig(status: string, kind?: 'application'): StatusConfig {
  if (kind === 'application') {
    const stage = getTaxonomyStatusConfig(status);
    if (stage) return stage;
  }
  const key = status?.toLowerCase().replace(/[_\s]+/g, '');
  const mapped = STATUS_MAP[key] || STATUS_MAP[status?.toLowerCase()];
  if (mapped) return mapped;
  // Premium Managed Hiring taxonomy (and legacy accepted/pending/rejected) → stage colours.
  const taxonomy = getTaxonomyStatusConfig(status);
  if (taxonomy) return taxonomy;
  return {
    label: status
      ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase().replace(/_/g, ' ')
      : '-',
    sx: { backgroundColor: 'var(--color-background-muted)', color: 'var(--color-text-secondary)' },
  };
}

export default function CRMStatusChip({ status, label, kind }: CRMStatusChipProps) {
  if (!status) return null;
  const config = getStatusConfig(status, kind);

  return (
    <Chip
      label={label ?? config.label}
      size="small"
      sx={{
        ...config.sx,
        fontWeight: 700,
        // --chip-font-size, not --font-size-3xs: the generic step is declared twice in
        // design-system.scss and the later declaration wins at 10px, so a chip using it
        // silently disagrees with every chip that uses the purpose-named token.
        fontSize: 'var(--chip-font-size)',
        height: 22,
        borderRadius: '6px',
        '& .MuiChip-label': { px: '9px' },
      }}
    />
  );
}
